import {
  searchAndReplaceInParagraph,
  searchAndReplaceInTableCell,
  addTableRows,
  addTableColumns,
  deleteTableRows,
  deleteTableColumns,
  insertParagraph,
  insertChainedParagraphs,
  insertTable,
  deleteParagraph,
} from "../../../utils/taskpane";
import { parseArgs } from "./parseArgs";

export interface ToolResult {
  success: boolean;
  error?: string;
  detail?: string;
  bookmarkName?: string;
  bookmarkNames?: string[];
}

// ── Arg shapes for each tool ────────────────────────────────────────────────

interface SearchAndReplaceArgs {
  segmentId?: string;
  paragraphText?: string;
  find?: string;
  replace?: string;
}

interface InsertParagraphArgs {
  segmentId?: string;
  paragraphText?: string;
  text?: string;
  position?: "before" | "after";
  style?: string;
  insertAfterBookmark?: string;
  listType?: "bullet" | "numbered";
}

interface DeleteParagraphArgs {
  segmentId?: string;
  paragraphText?: string;
}

interface TableCellArgs {
  tableIndex?: number;
  row?: number;
  column?: number;
  find?: string;
  replace?: string;
}

interface AddTableRowsArgs {
  tableIndex?: number;
  count?: number;
  position?: "start" | "end" | number;
  values?: string[][];
}

interface AddTableColumnsArgs {
  tableIndex?: number;
  count?: number;
  position?: "start" | "end" | number;
  values?: string[][];
}

interface DeleteTableRowsArgs {
  tableIndex?: number;
  rowIndex?: number;
  count?: number;
}

interface DeleteTableColumnsArgs {
  tableIndex?: number;
  columnIndex?: number;
  count?: number;
}

interface InsertChainedParagraphsArgs {
  segmentId?: string;
  paragraphText?: string;
  position?: "before" | "after";
  paragraphs?: Array<{ text: string; style?: string; listType?: "bullet" | "numbered" }>;
  insertAfterBookmark?: string;
}

interface InsertTableArgs {
  segmentId?: string;
  paragraphText?: string;
  rows?: number;
  columns?: number;
  position?: "before" | "after";
  values?: string[][];
  insertAfterBookmark?: string;
}

// ── Executor ────────────────────────────────────────────────────────────────

/**
 * Executes a single tool call and returns a normalised result.
 *
 * This is intentionally a plain async function (no React dependencies) so it
 * can be tested and reused independently of the component tree.
 */
export async function executeToolCall(name: string, rawArgs: object | string): Promise<ToolResult> {
  try {
    switch (name) {
      case "search_and_replace": {
        const args = parseArgs<SearchAndReplaceArgs>(rawArgs);
        if (!args.segmentId || !args.find || args.replace === undefined) {
          return {
            success: false,
            error: "Missing required arguments (segmentId, find, replace).",
          };
        }
        const result = await searchAndReplaceInParagraph(
          args.segmentId,
          args.find,
          args.replace,
          args.paragraphText
        );
        if (!result.success) {
          return { success: false, error: result.error ?? "Unknown error applying edit." };
        }
        const detail = result.warning
          ? `Replaced "${args.find}" with "${args.replace}" in ${args.segmentId}. ${result.warning}`
          : `Replaced "${args.find}" with "${args.replace}" in ${args.segmentId}.`;
        return { success: true, detail, bookmarkName: result.bookmarkName };
      }

      case "insert_paragraph": {
        const args = parseArgs<InsertParagraphArgs>(rawArgs);
        if (!args.segmentId || !args.text || !args.position) {
          return {
            success: false,
            error: "Missing required arguments (segmentId, text, position).",
          };
        }
        const result = await insertParagraph(
          args.segmentId,
          args.text,
          args.position,
          args.style,
          args.paragraphText,
          args.insertAfterBookmark,
          args.listType
        );
        if (!result.success) {
          return { success: false, error: result.error ?? "Unknown error inserting paragraph." };
        }
        return {
          success: true,
          detail: `Inserted paragraph ${args.position} ${args.segmentId}${args.style ? ` with style "${args.style}"` : ""}.`,
          bookmarkName: result.bookmarkName,
        };
      }

      case "delete_paragraph": {
        const args = parseArgs<DeleteParagraphArgs>(rawArgs);
        if (!args.segmentId) {
          return { success: false, error: "Missing required argument (segmentId)." };
        }
        const result = await deleteParagraph(args.segmentId, args.paragraphText);
        if (!result.success) {
          return { success: false, error: result.error ?? "Unknown error deleting paragraph." };
        }
        return {
          success: true,
          detail: `Deleted paragraph ${args.segmentId}.`,
          bookmarkName: result.bookmarkName,
        };
      }

      case "search_and_replace_in_table": {
        const args = parseArgs<TableCellArgs>(rawArgs);
        if (
          args.tableIndex === undefined ||
          args.row === undefined ||
          args.column === undefined ||
          args.find === undefined ||
          args.replace === undefined
        ) {
          return {
            success: false,
            error: "Missing required arguments (tableIndex, row, column, find, replace).",
          };
        }
        const result = await searchAndReplaceInTableCell(
          args.tableIndex,
          args.row,
          args.column,
          args.find,
          args.replace
        );
        if (!result.success) {
          return { success: false, error: result.error ?? "Unknown error editing table cell." };
        }
        const detail = result.warning
          ? `Replaced "${args.find}" with "${args.replace}" in table ${args.tableIndex} cell [${args.row},${args.column}]. ${result.warning}`
          : `Replaced "${args.find}" with "${args.replace}" in table ${args.tableIndex} cell [${args.row},${args.column}].`;
        return { success: true, detail, bookmarkName: result.bookmarkName };
      }

      case "add_table_rows": {
        const args = parseArgs<AddTableRowsArgs>(rawArgs);
        if (
          args.tableIndex === undefined ||
          args.count === undefined ||
          args.position === undefined
        ) {
          return {
            success: false,
            error: "Missing required arguments (tableIndex, count, position).",
          };
        }
        const result = await addTableRows(args.tableIndex, args.count, args.position, args.values);
        if (!result.success) {
          return { success: false, error: result.error ?? "Unknown error adding rows." };
        }
        return {
          success: true,
          detail: `Added ${args.count} row(s) to table ${args.tableIndex}.`,
          bookmarkName: result.bookmarkName,
        };
      }

      case "add_table_columns": {
        const args = parseArgs<AddTableColumnsArgs>(rawArgs);
        if (
          args.tableIndex === undefined ||
          args.count === undefined ||
          args.position === undefined
        ) {
          return {
            success: false,
            error: "Missing required arguments (tableIndex, count, position).",
          };
        }
        const result = await addTableColumns(
          args.tableIndex,
          args.count,
          args.position,
          args.values
        );
        if (!result.success) {
          return { success: false, error: result.error ?? "Unknown error adding columns." };
        }
        return {
          success: true,
          detail: `Added ${args.count} column(s) to table ${args.tableIndex}.`,
          bookmarkName: result.bookmarkName,
        };
      }

      case "delete_table_rows": {
        const args = parseArgs<DeleteTableRowsArgs>(rawArgs);
        if (
          args.tableIndex === undefined ||
          args.rowIndex === undefined ||
          args.count === undefined
        ) {
          return {
            success: false,
            error: "Missing required arguments (tableIndex, rowIndex, count).",
          };
        }
        const result = await deleteTableRows(args.tableIndex, args.rowIndex, args.count);
        if (!result.success) {
          return { success: false, error: result.error ?? "Unknown error deleting rows." };
        }
        return {
          success: true,
          detail: `Deleted ${args.count} row(s) from table ${args.tableIndex} starting at row ${args.rowIndex}.`,
          bookmarkName: result.bookmarkName,
        };
      }

      case "delete_table_columns": {
        const args = parseArgs<DeleteTableColumnsArgs>(rawArgs);
        if (
          args.tableIndex === undefined ||
          args.columnIndex === undefined ||
          args.count === undefined
        ) {
          return {
            success: false,
            error: "Missing required arguments (tableIndex, columnIndex, count).",
          };
        }
        const result = await deleteTableColumns(args.tableIndex, args.columnIndex, args.count);
        if (!result.success) {
          return { success: false, error: result.error ?? "Unknown error deleting columns." };
        }
        return {
          success: true,
          detail: `Deleted ${args.count} column(s) from table ${args.tableIndex} starting at column ${args.columnIndex}.`,
          bookmarkName: result.bookmarkName,
        };
      }

      case "insert_chained_paragraphs": {
        const args = parseArgs<InsertChainedParagraphsArgs>(rawArgs);
        if (!args.segmentId || !args.position || !args.paragraphs?.length) {
          return {
            success: false,
            error: "Missing required arguments (segmentId, position, paragraphs).",
          };
        }
        const result = await insertChainedParagraphs(
          args.segmentId,
          args.paragraphs,
          args.position,
          args.paragraphText,
          args.insertAfterBookmark
        );
        if (!result.success) {
          return { success: false, error: result.error ?? "Unknown error inserting paragraphs." };
        }
        return {
          success: true,
          detail: `Inserted ${args.paragraphs.length} paragraph(s) ${args.position} ${args.segmentId}.`,
          bookmarkName: result.bookmarkName,
          bookmarkNames: result.bookmarkNames,
        };
      }

      case "insert_table": {
        const args = parseArgs<InsertTableArgs>(rawArgs);
        if (
          !args.segmentId ||
          args.rows === undefined ||
          args.columns === undefined ||
          !args.position
        ) {
          return {
            success: false,
            error: "Missing required arguments (segmentId, rows, columns, position).",
          };
        }
        const result = await insertTable(
          args.segmentId,
          args.rows,
          args.columns,
          args.position,
          args.values,
          args.paragraphText,
          args.insertAfterBookmark
        );
        if (!result.success) {
          return { success: false, error: result.error ?? "Unknown error inserting table." };
        }
        return {
          success: true,
          detail: `Inserted ${args.rows}×${args.columns} table ${args.position} ${args.segmentId}.`,
          bookmarkName: result.bookmarkName,
        };
      }

      default:
        return { success: false, error: `Unknown tool: ${name}` };
    }
  } catch (toolError) {
    return { success: false, error: `Tool execution failed: ${String(toolError)}` };
  }
}
